/**
 * Shared utility for normalizing entity types to canonical ahoy_* format.
 *
 * Handles all legacy type aliases and ensures consistent mapping across the codebase.
 * This prevents divergence between components that need entity type normalization.
 */

import type { EntityType } from './types';
import { isEntityType } from './utils';

/**
 * `ahoy_validation_rule` is not part of the closed `EntityType` union (search
 * results never return validation rules), but chat-mention seeding still
 * needs to normalize it without the unknown-type console.warn below. Widening
 * to `string` first, then narrowing, is the same pattern used to normalize
 * arbitrary `entity_type_name` values elsewhere.
 */
const VALIDATION_RULE_TYPE: string = 'ahoy_validation_rule';

/**
 * `ahoy_automation_webhook` is not part of the closed `EntityType` union, but
 * the webhook "Create automation" CTA seeds a mention with this entity type and
 * needs normalization without triggering the unknown-type console.warn.
 */
const WEBHOOK_AUTOMATION_TYPE: string = 'ahoy_automation_webhook';

/**
 * Map of legacy type aliases to canonical ahoy_* types.
 * Used for reference and to ensure all aliases are handled consistently.
 */
export const ENTITY_TYPE_ALIASES: Record<string, EntityType> = {
  // User aliases
  teammate: 'ahoy_user',
  user: 'ahoy_user',
  ahoy_user: 'ahoy_user',

  // Contact aliases
  contact: 'ahoy_contact',
  person: 'ahoy_contact',
  ahoy_contact: 'ahoy_contact',

  // Company aliases
  company: 'ahoy_company',
  ahoy_company: 'ahoy_company',

  // Deal aliases
  deal: 'ahoy_deal',
  ahoy_deal: 'ahoy_deal',

  // Note aliases
  note: 'ahoy_note',
  ahoy_note: 'ahoy_note',

  // Validation rule alias — see VALIDATION_RULE_TYPE comment above.
  ahoy_validation_rule: VALIDATION_RULE_TYPE as EntityType,

  // Webhook automation alias — see WEBHOOK_AUTOMATION_TYPE comment above.
  ahoy_automation_webhook: WEBHOOK_AUTOMATION_TYPE as EntityType,

  // Backward compat: old _entity suffix forms (for stale localStorage data)
  ahoy_user_entity: 'ahoy_user',
  ahoy_contact_entity: 'ahoy_contact',
  ahoy_company_entity: 'ahoy_company',
  ahoy_deal_entity: 'ahoy_deal',
  ahoy_note_entity: 'ahoy_note',

  // Backward compat: old _timeline suffix forms (replaced with _entity in PR #1997)
  ahoy_user_timeline: 'ahoy_user',
  ahoy_contact_timeline: 'ahoy_contact',
  ahoy_company_timeline: 'ahoy_company',
  ahoy_deal_timeline: 'ahoy_deal',
  ahoy_note_timeline: 'ahoy_note',
};

/**
 * Normalize entity type to canonical ahoy_* form.
 *
 * Converts all legacy type aliases to their canonical counterparts:
 * - "teammate", "user", "ahoy_user" → "ahoy_user"
 * - "contact", "person", "ahoy_contact" → "ahoy_contact"
 * - "company", "ahoy_company" → "ahoy_company"
 * - "deal", "ahoy_deal" → "ahoy_deal"
 * - Canonical types pass through unchanged
 *
 * For unknown types (backward compatibility with stored data):
 * - Logs a warning to console
 * - Returns the original value unchanged
 * - This prevents breaking existing data while alerting developers
 *
 * @param type - Entity type to normalize (case-insensitive)
 * @returns Canonical EntityType or original value if unknown
 *
 * @example
 * ```ts
 * normalizeEntityType('teammate') // => 'ahoy_user'
 * normalizeEntityType('person') // => 'ahoy_contact'
 * normalizeEntityType('ahoy_user') // => 'ahoy_user'
 * normalizeEntityType('ahoy_contact') // => 'ahoy_contact'
 * normalizeEntityType('unknown') // => 'unknown' (with console warning)
 * ```
 */
export function normalizeEntityType(type: string): EntityType {
  const lowerType = type.toLowerCase();

  // Check if we have a known alias
  const normalized = ENTITY_TYPE_ALIASES[lowerType];

  if (normalized) {
    return normalized;
  }

  // Unknown type - warn and return original for backward compatibility
  // This handles edge cases where stored data may have types we don't recognize
  console.warn(
    'Unrecognized entity type during normalization:',
    type,
    '- returning original value. Expected one of:',
    Object.keys(ENTITY_TYPE_ALIASES).join(', ')
  );

  // Validate the original type - if it's somehow a valid EntityType, use it
  // Otherwise return as-is for maximum backward compatibility
  if (isEntityType(lowerType)) {
    return lowerType;
  }

  return type as EntityType;
}
